import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { QUALIFIKATIONEN, BUNDESLAENDER } from '../../lib/constants'
import { KATEGORIE_BY_VALUE } from '../../lib/matching/engine'
import { runMatchingAll } from '../../lib/matching/runner'
import type { MatchResult, MatchKategorieV2 } from '../../lib/matching/types'
import MatchBreakdown from './MatchBreakdown'
import './AdminPages.css'

interface MatchRow {
  id: string
  score: number
  kategorie: string
  knockout: boolean | null
  manuell_bestaetigt: boolean | null
  manuell_verworfen: boolean | null
  berechnung: MatchResult | null
  pflegekraefte: { id: string; vorname: string; nachname: string; qualifikation: string | null; bundesland: string | null; status: string }
  arbeitgeber: { id: string; name: string; einrichtungstyp: string | null; ort: string | null; bundesland: string | null }
}

const FALLBACK_KAT = { label: 'Bewertet', color: '#94A3B8', icon: '•' }

function katConfig(kategorie: string) {
  return KATEGORIE_BY_VALUE[kategorie as MatchKategorieV2] || FALLBACK_KAT
}

function scoreColor(score: number): string {
  if (score >= 80) return '#10B981'
  if (score >= 70) return '#3B82F6'
  if (score >= 60) return '#F59E0B'
  if (score >= 50) return '#F97316'
  return '#94A3B8'
}

export default function MatchingView() {
  const navigate = useNavigate()
  const [matches, setMatches] = useState<MatchRow[]>([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [search, setSearch] = useState('')
  const [onlyMatchable, setOnlyMatchable] = useState(true)
  const [expandedPk, setExpandedPk] = useState<string | null>(null)
  const [expandedMatch, setExpandedMatch] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState<string | null>(null)
  const [counts, setCounts] = useState({ pk: 0, ag: 0 })

  useEffect(() => {
    loadMatches()
    loadCounts()
  }, [])

  async function loadCounts() {
    const [{ count: pk }, { count: ag }] = await Promise.all([
      supabase.from('pflegekraefte').select('*', { count: 'exact', head: true }),
      supabase.from('arbeitgeber').select('*', { count: 'exact', head: true }).neq('status', 'abgesagt'),
    ])
    setCounts({ pk: pk ?? 0, ag: ag ?? 0 })
  }

  async function loadMatches() {
    setLoading(true)
    const { data, error } = await supabase
      .from('matches')
      .select(`
        id, score, kategorie, knockout, manuell_bestaetigt, manuell_verworfen, berechnung,
        pflegekraefte!inner(id, vorname, nachname, qualifikation, bundesland, status),
        arbeitgeber!inner(id, name, einrichtungstyp, ort, bundesland)
      `)
      .not('arbeitgeber_id', 'is', null)
      .order('score', { ascending: false })
      .limit(4000)
    if (!error && data) {
      setMatches(data as unknown as MatchRow[])
    } else if (error) {
      console.error('loadMatches error:', error)
    }
    setLoading(false)
  }

  async function handleRunMatching() {
    setRunning(true)
    try {
      const res = await runMatchingAll()
      alert(
        `✅ Matching abgeschlossen\n\n` +
          `${res.pflegekraefte} Pflegekräfte × ${res.einrichtungen} Einrichtungen\n` +
          `${res.matches} Matches berechnet`,
      )
      await loadMatches()
      await loadCounts()
    } catch (err) {
      console.error('Matching error:', err)
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler'
      alert(
        'Fehler beim Matching: ' +
          msg +
          '\n\nHinweis: Wurde die Migration 007_arbeitgeber_matching.sql in Supabase ausgeführt?',
      )
    }
    setRunning(false)
  }

  async function handleDownloadPdf(m: MatchRow) {
    if (!m.berechnung) return
    setPdfLoading(m.id)
    try {
      const { data: pk } = await supabase
        .from('pflegekraefte')
        .select('berufserfahrung_jahre')
        .eq('id', m.pflegekraefte.id)
        .single()

      const { downloadMatchPdf } = await import('./MatchPdf')
      await downloadMatchPdf({
        matchCode: 'M-' + m.id.slice(0, 6).toUpperCase(),
        einrichtungName: m.arbeitgeber.name,
        qualifikation:
          QUALIFIKATIONEN.find((q) => q.value === m.pflegekraefte.qualifikation)?.label ||
          m.pflegekraefte.qualifikation ||
          '–',
        erfahrung: (pk as { berufserfahrung_jahre?: number | null } | null)?.berufserfahrung_jahre ?? null,
        bundesland:
          BUNDESLAENDER.find((b) => b.value === m.pflegekraefte.bundesland)?.label || '–',
        result: m.berechnung,
        generatedAt: new Date(),
      })
    } catch (err) {
      console.error('PDF error:', err)
      alert('PDF konnte nicht erzeugt werden: ' + (err instanceof Error ? err.message : 'Unbekannter Fehler'))
    } finally {
      setPdfLoading(null)
    }
  }

  async function updateMatchStatus(id: string, bestaetigt: boolean | null, verworfen: boolean | null) {
    await supabase
      .from('matches')
      .update({ manuell_bestaetigt: bestaetigt, manuell_verworfen: verworfen, bearbeitet_am: new Date().toISOString() })
      .eq('id', id)
    setMatches((prev) =>
      prev.map((m) => (m.id === id ? { ...m, manuell_bestaetigt: bestaetigt, manuell_verworfen: verworfen } : m)),
    )
  }

  // Nach Pflegekraft gruppieren
  const groups = useMemo(() => {
    const map = new Map<string, { pk: MatchRow['pflegekraefte']; rows: MatchRow[] }>()
    for (const row of matches) {
      const id = row.pflegekraefte.id
      if (!map.has(id)) map.set(id, { pk: row.pflegekraefte, rows: [] })
      map.get(id)!.rows.push(row)
    }
    const list = Array.from(map.values())
    for (const g of list) g.rows.sort((a, b) => b.score - a.score)
    list.sort((a, b) => (b.rows[0]?.score ?? 0) - (a.rows[0]?.score ?? 0))
    return list
  }, [matches])

  const filteredGroups = useMemo(() => {
    const s = search.trim().toLowerCase()
    return groups
      .map((g) => ({
        ...g,
        rows: onlyMatchable ? g.rows.filter((r) => !r.knockout) : g.rows,
      }))
      .filter((g) => {
        if (onlyMatchable && g.rows.length === 0) return false
        if (!s) return true
        return `${g.pk.vorname} ${g.pk.nachname}`.toLowerCase().includes(s)
      })
  }, [groups, search, onlyMatchable])

  const stats = useMemo(() => {
    const top = matches.filter((m) => m.kategorie === 'sehr_stark' || m.kategorie === 'gut').length
    const ko = matches.filter((m) => m.knockout).length
    return { total: matches.length, top, ko }
  }, [matches])

  const qualLabel = (v: string | null) => QUALIFIKATIONEN.find((q) => q.value === v)?.label || v || '–'
  const blLabel = (v: string | null) => BUNDESLAENDER.find((b) => b.value === v)?.label || v || '–'

  if (loading) return <div className="table-loading"><div className="loading-spinner" /></div>

  return (
    <div className="admin-page animate-fade-in-up">
      <div className="page-header">
        <div>
          <h1>🎯 Matching</h1>
          <p className="page-subtitle">
            Jede Pflegekraft, abgeglichen mit jeder Einrichtung — inkl. nachvollziehbarer Berechnung.
          </p>
        </div>
        <button className="btn btn--primary" onClick={handleRunMatching} disabled={running}>
          {running ? '⏳ Matching läuft...' : '🔄 Matching neu berechnen'}
        </button>
      </div>

      <div className="matching-stats">
        <div className="stat-card stat-card--total">
          <span className="stat-value">{counts.pk}</span>
          <span className="stat-label">Pflegekräfte</span>
        </div>
        <div className="stat-card stat-card--total">
          <span className="stat-value">{counts.ag}</span>
          <span className="stat-label">Einrichtungen</span>
        </div>
        <div className="stat-card stat-card--gut">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Matches berechnet</span>
        </div>
        <div className="stat-card stat-card--top">
          <span className="stat-value">{stats.top}</span>
          <span className="stat-label">🏆 Starke Matches</span>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Pflegekraft suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="mv-checkbox">
          <input type="checkbox" checked={onlyMatchable} onChange={(e) => setOnlyMatchable(e.target.checked)} />
          Knock-outs ausblenden
        </label>
      </div>

      {matches.length === 0 ? (
        <div className="detail-section">
          <div className="empty-state">
            <span className="empty-icon">🎯</span>
            <p>Noch keine Matches berechnet.</p>
            <p className="empty-hint">
              Klicken Sie auf „Matching neu berechnen". Dafür müssen Pflegekräfte und Einrichtungen im System sein.
            </p>
          </div>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="detail-section">
          <div className="empty-state"><span className="empty-icon">🔍</span><p>Keine Treffer.</p></div>
        </div>
      ) : (
        <div className="mv-pk-list">
          {filteredGroups.map((g) => {
            const best = g.rows[0]
            const isOpen = expandedPk === g.pk.id
            return (
              <div key={g.pk.id} className={`mv-pk-card ${isOpen ? 'mv-pk-card--open' : ''}`}>
                <div className="mv-pk-head" onClick={() => setExpandedPk(isOpen ? null : g.pk.id)}>
                  <span className="mv-pk-toggle">{isOpen ? '▾' : '▸'}</span>
                  <div className="mv-pk-main">
                    <span className="mv-pk-name">👤 {g.pk.vorname} {g.pk.nachname}</span>
                    <span className="mv-pk-meta">
                      {qualLabel(g.pk.qualifikation)} · {blLabel(g.pk.bundesland)} · {g.rows.length} Einrichtungen bewertet
                    </span>
                  </div>
                  {best && (
                    <div className="mv-pk-best" style={{ borderColor: scoreColor(best.score) }}>
                      <span style={{ color: scoreColor(best.score) }}>{best.score}</span>
                      <small>bester Match</small>
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div className="mv-matches">
                    {g.rows.map((m) => {
                      const kat = katConfig(m.kategorie)
                      const open = expandedMatch === m.id
                      return (
                        <div key={m.id} className={`mv-match ${open ? 'mv-match--open' : ''}`}>
                          <div
                            className="mv-match-row"
                            onClick={() => setExpandedMatch(open ? null : m.id)}
                          >
                            <div className="mv-match-score" style={{ background: scoreColor(m.score) }}>
                              {m.score}
                            </div>
                            <div className="mv-match-info">
                              <span
                                className="mv-match-title"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/admin/arbeitgeber/${m.arbeitgeber.id}`)
                                }}
                              >
                                🏢 {m.arbeitgeber.name}
                              </span>
                              <span className="mv-match-sub">
                                {m.arbeitgeber.einrichtungstyp || 'Einrichtung'}
                                {m.arbeitgeber.ort ? ` · ${m.arbeitgeber.ort}` : ''}
                              </span>
                            </div>
                            <span className="mv-badge" style={{ background: kat.color }}>
                              {kat.icon} {kat.label}
                            </span>
                            <div className="mv-match-actions" onClick={(e) => e.stopPropagation()}>
                              {m.manuell_bestaetigt ? (
                                <span className="match-status match-status--confirmed">✅</span>
                              ) : m.manuell_verworfen ? (
                                <span className="match-status match-status--rejected">❌</span>
                              ) : (
                                <>
                                  <button className="action-btn action-btn--confirm" title="Bestätigen" onClick={() => updateMatchStatus(m.id, true, null)}>✓</button>
                                  <button className="action-btn action-btn--reject" title="Verwerfen" onClick={() => updateMatchStatus(m.id, null, true)}>✗</button>
                                </>
                              )}
                            </div>
                          </div>
                          {open && (
                            <div className="mv-match-detail">
                              {m.berechnung ? (
                                <>
                                  <div className="mv-match-detail-actions">
                                    <button
                                      className="btn btn--secondary btn--sm"
                                      onClick={() => handleDownloadPdf(m)}
                                      disabled={pdfLoading === m.id}
                                    >
                                      {pdfLoading === m.id ? '⏳ PDF wird erzeugt...' : '📄 PDF für Einrichtung erstellen'}
                                    </button>
                                  </div>
                                  <MatchBreakdown result={m.berechnung} />
                                </>
                              ) : (
                                <p className="mb-empty">
                                  Für diesen Match liegt keine Berechnung vor. Bitte „Matching neu berechnen".
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
